(() => {
  if (window.StudioCommerce) return;
  const root = window.Studio?.config?.root || window.Shopify?.routes?.root || '/';
  const url = path => root + path.replace(/^\//, '');
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const data = () => {
    try { return JSON.parse($('[data-st-commerce-i18n]')?.textContent || '{}'); } catch { return {}; }
  };
  const t = key => data()[key] || key.replaceAll('_', ' ');
  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency: currency || window.Studio?.config?.currency || window.Shopify?.currency?.active || 'USD', currencyDisplay: window.Studio?.config?.currencyCode ? 'code' : 'symbol'
  }).format(Number(cents || 0) / 100);
  const readStore = key => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; } };
  const writeStore = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch {} };
  const request = async (path, body) => {
    const response = await fetch(url(path), {
      method: body === undefined ? 'GET' : 'POST', credentials: 'same-origin',
      headers: body instanceof FormData ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || t('request_failed'));
    return result;
  };
  let queue = Promise.resolve(), pendingOperations = 0, cartFailures = 0;
  const pendingForms = new WeakSet();
  const serial = task => {
    pendingOperations++;
    const next = queue.then(task, task).finally(() => { pendingOperations--; });
    queue = next.catch(() => {}); return next;
  };
  const purchaseProduct = form => form.matches('[data-st-product-form]') || form.querySelector('[data-st-variant-id]') ? form.closest('studio-product') : null;
  const cartScope = scope => {
    const old = scope?.closest('[data-st-cart]');
    return $$('[data-st-cart]').find(node => node.dataset.sectionId === old?.dataset.sectionId) || old;
  };
  const currentLineKey = control => {
    const key = control.dataset.stLine || control.dataset.stRemove, scope = cartScope(control);
    if (!scope) throw new Error(t('request_failed'));
    const lines = $$('[data-st-line]', scope);
    if (lines.some(line => line.dataset.stLine === key)) return key;
    const identity = control.closest('[data-st-line-identity]')?.dataset.stLineIdentity;
    const matches = identity ? lines.filter(line => line.closest('[data-st-line-identity]')?.dataset.stLineIdentity === identity) : [];
    if (matches.length === 1) return matches[0].dataset.stLine;
    throw new Error(t('request_failed'));
  };
  const errors = (scope, message) => {
    if (!scope) return;
    let area = $('[data-st-error]', scope);
    if (!area) { area = document.createElement('p'); area.dataset.stError = ''; area.setAttribute('role', 'status'); scope.append(area); }
    area.textContent = message;
  };
  const openDialog = dialog => {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    dialog.querySelector('button, input, a')?.focus();
  };
  const refreshCart = async () => {
    const nodes = $$('[data-st-cart]');
    const ids = [...new Set(nodes.map(node => node.dataset.sectionId))].slice(0, 5);
    const current = new URL(location.href);
    current.searchParams.set('sections', ids.join(','));
    const [cart, sections] = await Promise.all([
      request('cart.js'), ids.length ? fetch(current, { credentials: 'same-origin' }).then(r => { if (!r.ok) throw new Error(t('request_failed')); return r.json(); }) : Promise.resolve({})
    ]);
    let incomplete = false;
    nodes.forEach(node => {
      const html = sections[node.dataset.sectionId];
      if (!html) { incomplete = true; return; }
      const replacement = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-st-cart]');
      if (!replacement) { incomplete = true; return; }
      const active = node.contains(document.activeElement) ? document.activeElement : null;
      let nextFocus;
      if (active?.matches('[data-st-line]')) nextFocus = $$('[data-st-line]', replacement).find(input => input.dataset.stLine === active.dataset.stLine) || $('[data-st-line], [name=checkout], a', replacement);
      else if (active?.matches('[data-st-cart-note]')) nextFocus = $('[data-st-cart-note]', replacement);
      else if (active?.matches('[data-st-discount]')) nextFocus = $('[data-st-discount]', replacement);
      else if (active?.matches('[data-st-remove]')) nextFocus = $('[data-st-line], [name=checkout], a', replacement);
      node.replaceWith(replacement);
      nextFocus?.focus({ preventScroll: true });
    });
    $$('[data-st-cart-count]').forEach(node => { node.textContent = cart.item_count; node.hidden = cart.item_count === 0; });
    document.dispatchEvent(new CustomEvent('studio:cart-updated', { detail: { cart } }));
    if (incomplete) throw new Error(t('request_failed'));
    return cart;
  };
  const afterAdd = async () => {
    try { await refreshCart(); } catch { location.assign(url('cart')); return; }
    if (window.Studio?.config?.cartType !== 'page' && $('#studio-cart-dialog')) openDialog($('#studio-cart-dialog'));
    else location.assign(url('cart'));
  };
  const productCache = new Map();
  const fetchProduct = handle => {
    if (!productCache.has(handle)) productCache.set(handle, request('products/' + encodeURIComponent(handle) + '.js').catch(error => { productCache.delete(handle); throw error; }));
    return productCache.get(handle);
  };
  const productCard = product => {
    const article = document.createElement('article'); article.className = 'st-product-card st-card';
    const link = document.createElement('a'); link.href = url('products/' + encodeURIComponent(product.handle));
    if (product.featured_image) {
      const image = document.createElement('img'); image.src = product.featured_image; image.alt = product.title;
      image.loading = 'lazy'; image.width = 600; image.height = 600; image.className = 'st-product-card__image'; link.append(image);
    }
    const title = document.createElement('h3'); title.textContent = product.title; link.append(title); article.append(link);
    const price = document.createElement('p'); price.className = 'st-price'; price.textContent = money(product.price); article.append(price);
    const action = document.createElement('a'); action.href = link.href; action.className = 'st-button'; action.textContent = t('choose_options'); article.append(action);
    return article;
  };
  class StudioProduct extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const initial = JSON.parse($('[data-st-variants]', this)?.textContent || '{"variants":[]}');
      this.variants = initial.variants || [];
      if (initial.selected) this.variants.unshift(initial.selected);
      this.variant = initial.selected || this.variants[0];
      this.gallery = $('.st-gallery', this);
      this.viewport = window.matchMedia?.('(min-width:750px)');
      this.viewport?.addEventListener('change', () => this.showMedia(this.activeMedia || this.variant?.media_id), { signal: this.abort.signal });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-st-option]')) {
          if (this.dataset.remoteOptions === 'true') this.selectRemote(event.target);
          else this.updateVariant(true);
        }
        if (event.target.matches('[data-st-selling-plan]')) this.applyVariant(this.variant);
        if (event.target.matches('[data-st-quantity], [data-st-sticky-quantity]')) this.syncQuantity(event.target);
        if (event.target.matches('[data-st-property-checkbox]')) {
          const output = $('[data-st-property-value]', event.target.closest('label'));
          output.value = event.target.checked ? event.target.dataset.checkedValue : event.target.dataset.uncheckedValue;
        }
      }, { signal: this.abort.signal });
      this.addEventListener('click', event => {
        const target = event.target.closest('[data-st-media-target]');
        if (target) this.showMedia(target.dataset.stMediaTarget, true);
        const step = event.target.closest('[data-st-media-step]');
        if (step) this.stepMedia(Number(step.dataset.stMediaStep));
        if (event.target.closest('[data-st-scroll-buy]')) {
          const buy = $('[data-st-add-form]', this); buy?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          $('[data-st-options] input, [data-st-options] select, [data-st-submit]', this)?.focus({ preventScroll: true });
        }
      }, { signal: this.abort.signal });
      let touchStart;
      this.gallery?.addEventListener('touchstart', event => { touchStart = event.touches[0].clientX; }, { passive: true, signal: this.abort.signal });
      this.gallery?.addEventListener('touchend', event => {
        if (event.target.closest('video, iframe, model-viewer, .st-gallery__thumbnails')) return;
        const distance = event.changedTouches[0].clientX - touchStart;
        if (Math.abs(distance) > 45) this.stepMedia(distance < 0 ? 1 : -1);
      }, { passive: true, signal: this.abort.signal });
      this.updateVariant(false);
      this.setupSticky();
      const modelData = $('[data-st-product-models]', this);
      if (modelData && window.Shopify?.loadFeatures) {
        const setup = () => {
          if (!this.isConnected || !window.ShopifyXR) return;
          try { window.ShopifyXR.addModels(JSON.parse(modelData.textContent)); window.ShopifyXR.setupXRElements(); } catch {}
        };
        if (window.ShopifyXR) setup();
        else { document.addEventListener('shopify_xr_initialized', setup, { once: true, signal: this.abort.signal }); window.Shopify.loadFeatures([{ name: 'shopify-xr', version: '1.0', onLoad: error => { if (!error) setup(); } }]); }
      }
      if (this.dataset.updateUrl === 'true') {
        const handle = this.dataset.productHandle;
        writeStore('studio.recent', [handle, ...readStore('studio.recent').filter(h => h !== handle)].slice(0, 30));
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.selectionController?.abort(); this.selectionVersion = (this.selectionVersion || 0) + 1; this.stickyObserver?.disconnect(); }
    eligibleMedia() {
      return $$('[data-st-media]', this).filter(media => this.gallery?.dataset.hideVariantMedia !== 'true' || media.dataset.variantMedia !== 'true' || media.dataset.stMedia === String(this.variant?.media_id));
    }
    showMedia(id, userAction = false) {
      const eligible = this.eligibleMedia();
      const active = eligible.find(media => media.dataset.stMedia === String(id)) || eligible[0];
      if (!active) return;
      this.activeMedia = active.dataset.stMedia;
      const gridMode = this.viewport?.matches && ['stacked', 'grid-mix', 'columns'].includes(this.gallery?.dataset.layout);
      $$('[data-st-media]', this).forEach(media => {
        media.hidden = !eligible.includes(media) || (!gridMode && media !== active);
        media.classList.toggle('st-gallery__item--active', media === active);
        if (media !== active) {
          $$('video', media).forEach(video => video.pause());
          $$('iframe', media).forEach(frame => {
            frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
            frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
          });
        }
      });
      $$('[data-st-media-target]', this).forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.stMediaTarget === this.activeMedia));
        button.hidden = !eligible.some(media => media.dataset.stMedia === button.dataset.stMediaTarget);
      });
      const count = $('[data-st-media-count]', this); if (count) count.textContent = `${eligible.indexOf(active) + 1} / ${eligible.length}`;
      if (userAction && gridMode) active.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    stepMedia(step) {
      const items = this.eligibleMedia(), index = items.findIndex(item => item.dataset.stMedia === this.activeMedia);
      const next = items[(index + step + items.length) % items.length]; if (next) this.showMedia(next.dataset.stMedia, true);
    }
    setupSticky() {
      const bar = $('[data-st-sticky-buy]', this), buy = $('[data-st-add-form]', this); if (!bar || !buy) return;
      const payment = $('[data-st-payment-home] .shopify-payment-button', this);
      const paymentHome = payment?.parentElement, slot = $('[data-st-sticky-payment-slot]', bar);
      const positionPayment = () => {
        if (bar.hidden || !slot || !paymentHome) return;
        const bounds = slot.getBoundingClientRect();
        paymentHome.style.setProperty('--st-payment-left', bounds.left + 'px');
        paymentHome.style.setProperty('--st-payment-top', bounds.top + 'px');
        paymentHome.style.setProperty('--st-payment-width', bounds.width + 'px');
      };
      const setVisible = visible => {
        const desktop = this.viewport?.matches;
        if ((bar.classList.contains('st-sticky-buy--desktop') && !desktop) || (bar.classList.contains('st-sticky-buy--mobile') && desktop)) visible = false;
        bar.hidden = !visible;
        if (payment && slot) { paymentHome.classList.toggle('st-payment--sticky', visible); positionPayment(); }
      };
      if ('IntersectionObserver' in window) {
        this.stickyObserver = new IntersectionObserver(entries => setVisible(!entries[0].isIntersecting && entries[0].boundingClientRect.top < 0));
        this.stickyObserver.observe(buy);
      }
      this.viewport?.addEventListener('change', () => {
        const bounds = buy.getBoundingClientRect(); setVisible(bounds.bottom < 0);
      }, { signal: this.abort.signal });
      window.addEventListener('resize', positionPayment, { signal: this.abort.signal });
    }
    syncQuantity(source) {
      $$('[data-st-quantity], [data-st-sticky-quantity]', this).forEach(input => { if (input !== source) input.value = source.value; });
    }
    async selectRemote(input) {
      const option = input.tagName === 'SELECT' ? input.selectedOptions[0] : input;
      const selected = $$('[data-st-option]', this).filter(control => control.tagName === 'SELECT' || control.checked).map(control => control.tagName === 'SELECT' ? control.selectedOptions[0] : control);
      const ids = selected.map(control => control.dataset.optionId).filter(Boolean);
      const destination = new URL(option.dataset.productUrl || this.dataset.productUrl, location.origin);
      if (option.dataset.productUrl && destination.pathname !== new URL(this.dataset.productUrl, location.origin).pathname) {
        if (ids.length) destination.searchParams.set('option_values', ids.join(','));
        location.assign(destination); return;
      }
      if (!ids.length) { this.updateVariant(true); return; }
      destination.searchParams.set('option_values', ids.join(',')); destination.searchParams.delete('variant'); destination.searchParams.set('section_id', 'studio-product-state');
      this.selectionController?.abort(); this.selectionController = new AbortController();
      const version = this.selectionVersion = (this.selectionVersion || 0) + 1;
      this.pendingSelection = true; this.setAttribute('aria-busy', 'true');
      $$('[data-st-submit]', this).forEach(button => { button.disabled = true; });
      try {
        const response = await fetch(destination, { credentials: 'same-origin', signal: this.selectionController.signal });
        if (!response.ok) throw new Error(t('request_failed'));
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isConnected || version !== this.selectionVersion) return;
        const state = JSON.parse($('[data-st-product-state]', doc)?.textContent || 'null'); if (!state) throw new Error(t('request_failed'));
        this.variant = state.variant;
        for (const group of state.options || []) for (const value of group.values) {
          $$('[data-option-id="' + value.id + '"]', this).forEach(control => {
            control.dataset.available = String(value.available);
            control.closest('.st-option')?.classList.toggle('st-option--unavailable', !value.available);
            if (control.tagName === 'OPTION') control.textContent = value.name + (value.available ? '' : ' — ' + t('sold_out'));
          });
        }
        $$('[data-st-pickup]', this).forEach(target => { target.innerHTML = $('[data-st-pickup]', doc)?.innerHTML || ''; });
        $$('[data-st-volume-pricing]', this).forEach(target => { target.innerHTML = $('[data-st-volume-pricing]', doc)?.innerHTML || ''; });
        this.pendingSelection = false; this.applyVariant(state.variant);
        if (this.dataset.updateUrl === 'true') {
          destination.searchParams.delete('section_id');
          if (state.variant) { destination.searchParams.delete('option_values'); destination.searchParams.set('variant', state.variant.id); }
          history.replaceState({}, '', destination);
        }
        errors($('[data-st-add-form]', this) || this, '');
      } catch (error) {
        if (error.name === 'AbortError' || version !== this.selectionVersion || !this.isConnected) return;
        this.variant = null; this.pendingSelection = false; this.applyVariant(null);
        errors($('[data-st-add-form]', this) || this, error.message);
      } finally {
        if (version === this.selectionVersion && !this.pendingSelection) this.removeAttribute('aria-busy');
      }
    }
    updateVariant(changed) {
      const selected = [];
      $$('[data-st-option]', this).forEach(input => {
        if (input.tagName === 'SELECT' || input.checked) selected[Number(input.dataset.stOption) - 1] = input.value;
      });
      const fallback = $('[data-st-variant-id]', this)?.value;
      const variant = selected.length ? this.variants.find(item => item.options.every((option, index) => option === selected[index])) : this.variants.find(item => String(item.id) === fallback) || this.variants[0];
      this.variant = variant;
      this.applyVariant(variant);
      if (changed && variant && this.dataset.updateUrl === 'true') {
        const next = new URL(location.href); next.searchParams.set('variant', variant.id); history.replaceState({}, '', next);
      }
    }
    applyVariant(variant) {
      const available = variant?.available && !this.pendingSelection;
      $$('[data-st-submit]', this).forEach(button => {
        button.disabled = !available || pendingForms.has(button.form);
        button.textContent = available ? this.dataset.addLabel : variant ? this.dataset.soldLabel : this.dataset.unavailableLabel;
      });
      $$('[data-st-variant-id]', this).forEach(input => { input.value = variant?.id || ''; input.disabled = !variant; });
      $$('[data-st-sold-badge]', this).forEach(node => { node.hidden = !!available; });
      if (!variant) {
        $$('[data-st-price]', this).forEach(node => { node.textContent = this.dataset.unavailableLabel; });
        $$('[data-st-compare-price],[data-st-unit-price],[data-st-sku],[data-st-barcode]', this).forEach(node => { node.textContent = ''; });
        $$('[data-st-payment-home], [data-st-sticky-payment-slot]', this).forEach(node => { node.hidden = true; });
        $$('[data-st-sale-badge]', this).forEach(node => { node.hidden = true; });
        const inventory = $('[data-st-inventory]', this); if (inventory) inventory.textContent = this.dataset.unavailableLabel;
        return;
      }
      $$('[data-st-payment-home], [data-st-sticky-payment-slot]', this).forEach(node => { node.hidden = !available; });
      const plan = $('[data-st-selling-plan]', this);
      let allocation;
      if (plan) {
        $$('option', plan).forEach(option => {
          option.disabled = !!option.value && !variant.selling_plan_allocations?.some(item => String(item.selling_plan.id) === option.value);
        });
        if (plan.selectedOptions[0]?.disabled) plan.value = $$('option', plan).find(option => !option.disabled)?.value || '';
        allocation = variant.selling_plan_allocations?.find(item => String(item.selling_plan.id) === plan.value);
        if (plan.dataset.required === 'true' && !allocation) {
          $$('[data-st-submit]', this).forEach(button => { button.disabled = true; button.textContent = this.dataset.unavailableLabel; });
          $$('[data-st-payment-home], [data-st-sticky-payment-slot]', this).forEach(node => { node.hidden = true; });
        }
      }
      const price = allocation?.price ?? variant.price;
      const comparePrice = allocation?.compare_at_price ?? variant.compare_at_price;
      $$('[data-st-price]', this).forEach(node => { node.textContent = money(price, this.dataset.currency); });
      $$('[data-st-compare-price]', this).forEach(node => {
        node.hidden = !comparePrice || comparePrice <= price;
        node.textContent = money(comparePrice, this.dataset.currency);
      });
      $$('[data-st-sale-badge]', this).forEach(node => {
        node.hidden = !comparePrice || comparePrice <= price;
        const saved = (comparePrice || 0) - price;
        node.textContent = node.dataset.badgeType === 'percentage_off' ? (node.dataset.saleLabel || '__PERCENT__% off').replace('__PERCENT__', String(Math.round(saved / comparePrice * 100))) : node.dataset.badgeType === 'percentage' ? '−' + Math.round(saved / comparePrice * 100) + '%' : ['amount', 'fixed_amount'].includes(node.dataset.badgeType) ? '−' + money(saved, this.dataset.currency) : node.dataset.saleLabel;
      });
      const unit = $('[data-st-unit-price]', this);
      const unitPrice = allocation?.unit_price ?? variant.unit_price;
      if (unit) unit.textContent = unitPrice && variant.unit_price_measurement ? money(unitPrice, this.dataset.currency) + '/' + variant.unit_price_measurement.reference_value + variant.unit_price_measurement.reference_unit : '';
      const sku = $('[data-st-sku]', this); if (sku) sku.textContent = variant.sku || '';
      const barcode = $('[data-st-barcode]', this); if (barcode) barcode.textContent = variant.barcode || '';
      const inventory = $('[data-st-inventory]', this);
      if (inventory) inventory.textContent = !available ? t('sold_out') : inventory.dataset.showQuantity === 'true' && variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= Number(inventory.dataset.threshold) ? t('remaining_stock').replace('__COUNT__', variant.inventory_quantity) : t('in_stock');
      $$('[data-st-quantity], [data-st-sticky-quantity]', this).forEach(input => {
        const rule = variant.quantity_rule || {}; input.min = rule.min || 1; input.step = rule.increment || 1;
        if (rule.max) input.max = rule.max; else input.removeAttribute('max');
        let next = Math.max(Number(input.min), Number(input.value) || Number(input.min));
        next = Number(input.min) + Math.ceil((next - Number(input.min)) / Number(input.step)) * Number(input.step);
        if (rule.max && next > rule.max) next = rule.min + Math.floor((rule.max - rule.min) / rule.increment) * rule.increment;
        input.value = next;
      });
      this.showMedia(variant.media_id || this.activeMedia);
    }
  }
  class StudioRecommendations extends HTMLElement {
    connectedCallback() {
      if ($('.st-product-card', $('[data-st-recommendation-content]', this) || this)) return;
      this.observer = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) { this.observer.disconnect(); this.load(); } }, { rootMargin: '250px' });
      this.observer.observe(this);
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    async load() {
      try {
        const html = await fetch(this.dataset.url).then(response => { if (!response.ok) throw new Error(); return response.text(); });
        const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-st-recommendation-content]');
        const excluded = (this.dataset.excludeHandles || '').split(',');
        if (content) {
          $$('[data-product-handle]', content).forEach(card => { if (excluded.includes(card.dataset.productHandle)) card.remove(); });
          const grid = $('.st-products-grid', content);
          if (grid && this.dataset.columns) grid.style.setProperty('--st-columns', this.dataset.columns);
          if (this.dataset.layout === 'horizontal-list' || this.dataset.layout === 'horizontal') {
            grid?.classList.add('st-recommendations-list');
            $$('.st-product-card', content).forEach(card => card.classList.add('st-product-card--horizontal'));
          }
          if (this.dataset.imageRatio && this.dataset.imageRatio !== 'adapt') $$('.st-product-card', content).forEach(card => card.style.setProperty('--st-image-ratio', this.dataset.imageRatio));
          if (this.dataset.quickAdd === 'false') $$('.st-card-add, [data-st-add-form]', content).forEach(form => form.remove());
        }
        if (content?.querySelector('.st-product-card')) $('[data-st-recommendation-content]', this).replaceChildren(...content.childNodes);
        else this.hidden = true;
      } catch { this.hidden = true; }
    }
  }
  class StudioRecentProducts extends HTMLElement {
    async connectedCallback() {
      const current = $('studio-product')?.dataset.productHandle;
      const handles = readStore('studio.recent').filter(handle => handle !== current).slice(0, Number(this.dataset.limit) || 8);
      const products = await Promise.allSettled(handles.map(fetchProduct));
      this.replaceChildren(...products.filter(result => result.status === 'fulfilled').map(result => productCard(result.value)));
      if (this.children.length) this.closest('[data-st-recent-section]').hidden = false;
    }
  }
  class StudioProductTabs extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.addEventListener('click', event => { if (event.target.closest('[role=tab]')) this.select(event.target.closest('[role=tab]')); }, { signal: this.abort.signal });
      this.addEventListener('keydown', event => {
        if (!event.target.matches('[role=tab]')) return;
        const tabs = $$('[role=tab]', this), index = tabs.indexOf(event.target);
        let next;
        if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
        if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
        if (event.key === 'Home') next = tabs[0]; if (event.key === 'End') next = tabs.at(-1);
        if (next) { event.preventDefault(); this.select(next); next.focus(); }
      }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); }
    select(tab) {
      $$('[role=tab]', this).forEach(button => { button.setAttribute('aria-selected', String(button === tab)); button.tabIndex = button === tab ? 0 : -1; });
      $$('[role=tabpanel]', this).forEach(panel => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
    }
  }
  const comparisonTable = (products, fields, removable) => {
    const wrapper = document.createElement('div'); wrapper.className = 'st-table-scroll';
    const table = document.createElement('table'); table.className = 'st-table'; wrapper.append(table);
    const header = document.createElement('tr'); header.append(document.createElement('th'));
    products.forEach(product => {
      const cell = document.createElement('th'); cell.scope = 'col'; cell.append(productCard(product));
      if (removable) { const remove = document.createElement('button'); remove.type = 'button'; remove.dataset.stCompareRemove = product.handle; remove.textContent = t('remove'); cell.append(remove); }
      header.append(cell);
    });
    const head = document.createElement('thead'); head.append(header); table.append(head);
    const body = document.createElement('tbody'); table.append(body);
    (fields.length ? fields : ['price', 'vendor', 'type', 'variants', 'description']).filter(field => field !== 'info').forEach(field => {
      const row = document.createElement('tr'), heading = document.createElement('th'); heading.scope = 'row'; heading.textContent = t(field); row.append(heading);
      products.forEach(product => {
        const cell = document.createElement('td');
        if (field === 'price') cell.textContent = money(product.price);
        else if (field === 'variants') cell.textContent = product.variants.map(variant => variant.title).join(', ');
        else if (field === 'description') { const parsed = new DOMParser().parseFromString(product.description || '', 'text/html'); cell.textContent = parsed.body.textContent; }
        else cell.textContent = product[field] || '—';
        row.append(cell);
      }); body.append(row);
    });
    return wrapper;
  };
  class StudioComparison extends HTMLElement {
    connectedCallback() { this.load(); }
    async load() {
      const products = await Promise.allSettled(readStore('studio.compare').slice(0, 4).map(fetchProduct));
      const good = products.filter(result => result.status === 'fulfilled').map(result => result.value);
      if (good.length) this.replaceChildren(comparisonTable(good, (this.dataset.fields || '').split(',').filter(Boolean), true));
      else this.textContent = t('compare_empty');
    }
  }
  class StudioRelatedComparison extends HTMLElement {
    async connectedCallback() {
      try {
        const [current, result] = await Promise.all([fetchProduct(this.dataset.current), fetch(this.dataset.url).then(r => r.json())]);
        this.replaceChildren(comparisonTable([current, ...(result.products || [])], (this.dataset.fields || '').split(',').filter(Boolean), false));
      } catch { this.hidden = true; }
    }
  }
  class StudioCountdown extends HTMLElement {
    connectedCallback() {
      clearInterval(this.timer);
      const fixed = () => {
        const value = this.dataset.end || '', parts = value.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
        if (!parts) return new Date(value).getTime();
        let hour = Number(parts[4]); if (parts[7]) hour = hour % 12 + (parts[7].toUpperCase() === 'PM' ? 12 : 0);
        return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]), hour, Number(parts[5]), Number(parts[6] || 0)).getTime();
      };
      const recurring = () => {
        const now = new Date(), duration = this.dataset.duration || 'every_24_hrs';
        let end;
        if (duration.includes('month')) end = new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime();
        else if (duration.includes('week')) end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (7 - now.getDay())).getTime();
        else {
          const intervalHours = Number(duration.match(/every_(\d+)_hrs/)?.[1]) || 24;
          const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
          const interval = intervalHours * 3600000;
          end = midnight + (Math.floor((now.getTime() - midnight) / interval) + 1) * interval;
        }
        return end;
      };
      let end = this.dataset.mode === 'evergreen' ? recurring() : fixed();
      const update = () => {
        if (end <= Date.now() && this.dataset.mode === 'evergreen') end = recurring();
        const seconds = Math.max(0, Math.floor((end - Date.now()) / 1000));
        if (!Number.isFinite(seconds) || !seconds) { this.hidden = true; clearInterval(this.timer); return; }
        this.hidden = false; this.endTime = end;
        $('output', this).textContent = `${Math.floor(seconds / 86400)}${t('countdown_days')} ${Math.floor(seconds % 86400 / 3600)}${t('countdown_hours')} ${Math.floor(seconds % 3600 / 60)}${t('countdown_minutes')} ${seconds % 60}${t('countdown_seconds')}`;
      }; update(); if (!this.hidden) this.timer = setInterval(update, 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  class StudioShipping extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const country = $('[data-st-country]', this), province = $('[data-st-province]', this);
      const populate = () => {
        const values = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]');
        province.replaceChildren(...values.map(([value, label]) => { const option = document.createElement('option'); option.value = value; option.textContent = label; return option; }));
        province.closest('label').hidden = !values.length;
      };
      const countryCode = window.Shopify?.country;
      const countryMatch = [...country.options].find(option => option.value === countryCode || option.dataset.code === countryCode);
      if (countryMatch) country.value = countryMatch.value;
      populate();
      country.addEventListener('change', populate, { signal: this.abort.signal });
      $('[data-st-calculate]', this).addEventListener('click', async event => {
        const result = $('[data-st-rates]', this), button = event.currentTarget; button.disabled = true; result.textContent = t('loading');
        const query = new URLSearchParams({ 'shipping_address[country]': country.value, 'shipping_address[province]': province.value, 'shipping_address[zip]': $('[data-st-zip]', this).value });
        try {
          await request('cart/prepare_shipping_rates.json?' + query, {});
          let rates;
          for (let attempt = 0; attempt < 8; attempt++) {
            rates = await request('cart/async_shipping_rates.json?' + query);
            if (rates?.shipping_rates) break;
            await new Promise(resolve => setTimeout(resolve, 750));
          }
          result.replaceChildren();
          if (!rates?.shipping_rates?.length) result.textContent = t('no_shipping_rates');
          rates?.shipping_rates?.forEach(rate => { const item = document.createElement('p'); item.textContent = rate.name + ': ' + money(Number(rate.price) * 100); result.append(item); });
        } catch (error) { result.textContent = error.message; } finally { button.disabled = false; }
      }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  class StudioBundleProduct extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.addEventListener('change', event => { if (event.target.matches('[data-st-bundle-option]')) this.select(event.target); }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); this.request?.abort(); this.version = (this.version || 0) + 1; }
    async select(input) {
      this.request?.abort(); this.request = new AbortController();
      const version = this.version = (this.version || 0) + 1;
      const checked = $('[data-st-bundle-check]', this).checked;
      const quantity = Number($('[data-st-bundle-quantity]', this).value);
      const option = input.selectedOptions[0];
      const endpoint = new URL(option.dataset.productUrl || this.dataset.productUrl, location.origin);
      endpoint.searchParams.set('option_values', $$('[data-st-bundle-option]', this).map(control => control.value).join(','));
      endpoint.searchParams.delete('variant'); endpoint.searchParams.set('section_id', 'studio-bundle-state');
      this.setAttribute('aria-busy', 'true'); $('[data-st-bundle-check]', this).disabled = true;
      $('[data-st-bundle-variant]', this).disabled = true;
      $('[data-st-bundle-error]', this).textContent = t('loading');
      try {
        const response = await fetch(endpoint, { credentials: 'same-origin', signal: this.request.signal });
        if (!response.ok) throw new Error(t('request_failed'));
        const replacement = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('studio-bundle-product');
        if (version !== this.version || !this.isConnected) return;
        if (!replacement) throw new Error(t('request_failed'));
        const nextCheck = $('[data-st-bundle-check]', replacement), nextQuantity = $('[data-st-bundle-quantity]', replacement);
        nextCheck.checked = checked && !nextCheck.disabled;
        const min = Number(nextQuantity.min) || 1, step = Number(nextQuantity.step) || 1;
        let next = min + Math.max(0, Math.ceil(((quantity || min) - min) / step)) * step;
        if (nextQuantity.max) next = Math.min(next, min + Math.floor((Number(nextQuantity.max) - min) / step) * step);
        nextQuantity.value = String(next); nextQuantity.disabled = !nextCheck.checked || nextCheck.disabled;
        const shouldFocus = document.activeElement === input, position = input.dataset.stBundleOption;
        const form = this.closest('[data-st-bundle-form]');
        this.replaceWith(replacement);
        if (shouldFocus) $$('[data-st-bundle-option]', replacement).find(control => control.dataset.stBundleOption === position)?.focus({ preventScroll: true });
        if (form) bundleTotal(form);
      } catch (error) {
        if (error.name === 'AbortError' || version !== this.version || !this.isConnected) return;
        $('[data-st-bundle-error]', this).textContent = error.message;
      } finally {
        if (version === this.version) this.removeAttribute('aria-busy');
      }
    }
  }
  class StudioQuickOrder extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.addEventListener('click', event => {
        const link = event.target.closest('[data-st-order-more]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); if (!this.loading) this.load(link);
      }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); this.request?.abort(); }
    async load(link) {
      this.loading = true; this.request?.abort(); this.request = new AbortController();
      const status = $('[data-st-order-status]', this), form = $('[data-st-bundle-form]', this);
      status.textContent = t('loading'); link.setAttribute('aria-disabled', 'true'); this.setAttribute('aria-busy', 'true');
      const endpoint = new URL(link.href, location.origin); endpoint.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(endpoint, { credentials: 'same-origin', signal: this.request.signal });
        if (!response.ok) throw new Error(t('request_failed'));
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isConnected) return;
        const next = $$('studio-quick-order', parsed).find(node => node.dataset.productId === this.dataset.productId && node.dataset.sectionId === this.dataset.sectionId);
        if (!next) throw new Error(t('request_failed'));
        const existing = new Set($$('[data-order-variant]', this).map(row => row.dataset.orderVariant));
        const rows = $$('[data-st-order-rows] > tr', next).filter(row => !existing.has(row.dataset.orderVariant));
        if (!rows.length) throw new Error(t('request_failed'));
        $('[data-st-order-rows]', this).append(...rows);
        const focused = document.activeElement === link;
        $('[data-st-order-pagination]', this).replaceChildren(...$('[data-st-order-pagination]', next).childNodes);
        status.textContent = t('variants_loaded').replace('__COUNT__', $$('[data-order-variant]', this).length);
        if (focused) { const nextLink = $('[data-st-order-more]', this); if (nextLink) nextLink.focus({ preventScroll: true }); else { status.tabIndex = -1; status.focus({ preventScroll: true }); } }
        bundleTotal(form);
      } catch (error) { if (error.name !== 'AbortError') status.textContent = error.message; }
      finally { this.loading = false; this.removeAttribute('aria-busy'); link.removeAttribute('aria-disabled'); }
    }
  }
  const definitions = { 'studio-product': StudioProduct, 'studio-recommendations': StudioRecommendations, 'studio-recent-products': StudioRecentProducts, 'studio-product-tabs': StudioProductTabs, 'studio-comparison': StudioComparison, 'studio-related-comparison': StudioRelatedComparison, 'studio-countdown': StudioCountdown, 'studio-shipping': StudioShipping, 'studio-bundle-product': StudioBundleProduct, 'studio-quick-order': StudioQuickOrder };
  Object.entries(definitions).forEach(([name, Component]) => { if (!customElements.get(name)) customElements.define(name, Component); });
  const selectedBundleItems = form => {
    const quick = form.hasAttribute('data-st-quick-order');
    return $$('.st-bundle-row', form).flatMap(row => {
      const check = $('[data-st-bundle-check]', row), input = $('[data-st-bundle-variant]', row), quantityInput = $('[data-st-bundle-quantity]', row);
      const quantity = Number(quantityInput?.value || 0);
      if ((!quick && (!check?.checked || check.disabled)) || (quick && quantity === 0) || quantityInput?.disabled) return [];
      const option = input.selectedOptions?.[0] || input;
      return [{ id: Number(input.value), productId: row.dataset.productId || input.value, quantity, disabled: input.disabled || option.disabled, valid: quantityInput?.checkValidity() !== false, price: Number(option.dataset.price || 0), min: Number(option.dataset.minimum || 1), step: Number(option.dataset.increment || 1), max: Number(option.dataset.maximum || 0) }];
    });
  };
  const bundleTotal = form => {
    if (!form.hasAttribute('data-st-quick-order')) $$('.st-bundle-row', form).forEach(row => {
      const check = $('[data-st-bundle-check]', row), quantity = $('[data-st-bundle-quantity]', row);
      if (check && quantity) quantity.disabled = !check.checked || check.disabled;
    });
    const total = $('[data-st-bundle-total]', form);
    const items = selectedBundleItems(form), quantity = items.reduce((sum, item) => sum + item.quantity, 0);
    if (total) total.textContent = money(items.reduce((sum, item) => sum + item.price * item.quantity, 0), form.dataset.currency);
    $$('[data-st-bundle-tier]', form).forEach(node => { node.classList.toggle('st-bundle-tier--reached', quantity >= Number(node.dataset.stBundleTier)); });
    const progress = $('[data-st-bundle-progress]', form); if (progress) progress.value = quantity;
  };
  $$('[data-st-bundle-form]').forEach(bundleTotal);
  document.addEventListener('shopify:section:load', event => $$('[data-st-bundle-form]', event.target).forEach(bundleTotal));
  document.addEventListener('submit', event => {
    const form = event.target;
    if (form.matches('.st-cart-form') && pendingOperations) {
      event.preventDefault();
      if (pendingForms.has(form)) return;
      pendingForms.add(form); form.setAttribute('aria-busy', 'true');
      const sectionId = form.closest('[data-st-cart]')?.dataset.sectionId;
      const submitterName = event.submitter?.name, submitterValue = event.submitter?.value;
      const failureCount = cartFailures;
      (async () => {
        while (pendingOperations) await queue;
        pendingForms.delete(form); form.removeAttribute('aria-busy');
        const nextForm = $$('[data-st-cart]').find(node => node.dataset.sectionId === sectionId)?.querySelector('.st-cart-form');
        if (!nextForm || cartFailures !== failureCount) return;
        const submitter = [...nextForm.elements].find(control => control.type === 'submit' && control.name === submitterName && control.value === submitterValue);
        nextForm.requestSubmit(submitter);
      })();
      return;
    }
    if (!form.matches('[data-st-add-form], [data-st-bundle-form]')) return;
    event.preventDefault();
    if (pendingForms.has(form)) return;
    const isBundle = form.matches('[data-st-bundle-form]'), product = purchaseProduct(form);
    if (!isBundle && !form.reportValidity()) return;
    let body;
    try {
      if (isBundle) {
        if (form.querySelector('studio-bundle-product[aria-busy="true"]')) throw new Error(t('loading'));
        const items = selectedBundleItems(form); if (!items.length) throw new Error(t('select_items'));
        const max = Number(form.dataset.maxItems); if (max && items.reduce((sum, item) => sum + item.quantity, 0) > max) throw new Error(t('bundle_limit').replace('__COUNT__', max));
        if (form.dataset.preventDuplicate === 'true' && (new Set(items.map(item => item.productId)).size !== items.length || items.some(item => item.quantity > 1))) throw new Error(t('duplicate_items'));
        if (items.some(item => item.disabled || !Number.isSafeInteger(item.id) || item.id <= 0)) throw new Error(t('unavailable'));
        if (items.some(item => !item.valid || !Number.isSafeInteger(item.quantity) || item.quantity < item.min || (item.quantity - item.min) % item.step || item.max && item.quantity > item.max)) throw new Error(t('quantity_rule'));
        body = { items: items.map(({ id, quantity }) => ({ id, quantity })) };
      } else {
        if (product?.pendingSelection || product && !product.variant?.available) throw new Error(t('unavailable'));
        body = new FormData(form);
        [...form.elements].filter(input => input.matches('[data-st-property-checkbox]')).forEach(input => body.set(input.dataset.propertyName, input.checked ? input.dataset.checkedValue : input.dataset.uncheckedValue));
      }
    } catch (error) { errors(form, error.message); return; }
    const buttons = [...form.elements].filter(control => ['submit', 'image'].includes(control.type));
    const disabled = buttons.map(button => button.disabled);
    buttons.forEach(button => { button.disabled = true; }); pendingForms.add(form); form.setAttribute('aria-busy', 'true'); errors(form, '');
    serial(async () => {
      try {
        await request('cart/add.js', body);
        await afterAdd();
      } catch (error) { cartFailures++; errors(form, error.message); } finally {
        pendingForms.delete(form); form.removeAttribute('aria-busy');
        buttons.forEach((button, index) => { button.disabled = disabled[index]; });
        if (product?.isConnected) product.applyVariant(product.variant);
      }
    });
  }, true);
  document.addEventListener('change', event => {
    const input = event.target;
    const form = input.closest('[data-st-bundle-form]');
    if (form) {
      if (input.matches('[data-st-bundle-variant]')) {
        const option = input.selectedOptions?.[0] || input, quantity = $('[data-st-bundle-quantity]', input.closest('.st-bundle-row'));
        quantity.min = option.dataset.minimum || 1; quantity.step = option.dataset.increment || 1;
        if (option.dataset.maximum) quantity.max = option.dataset.maximum; else quantity.removeAttribute('max');
        quantity.value = quantity.min;
      }
      bundleTotal(form);
    }
    if (input.matches('[data-st-line]')) {
      input.setCustomValidity('');
      const value = Number(input.value), minimum = Number(input.dataset.minimum || 1), increment = Number(input.dataset.increment || 1);
      if (!input.value.trim() || !Number.isSafeInteger(value) || value !== 0 && (value < minimum || (value - minimum) % increment)) input.setCustomValidity(t('quantity_rule'));
      if (!input.reportValidity()) return;
      const quantity = Number(input.value);
      input.readOnly = true; input.setAttribute('aria-busy', 'true');
      serial(async () => {
        try { await request('cart/change.js', { id: currentLineKey(input), quantity }); await refreshCart(); }
        catch (error) { cartFailures++; errors(cartScope(input), error.message); }
        finally { input.readOnly = false; input.removeAttribute('aria-busy'); }
      });
    }
    if (input.matches('[data-st-cart-note]')) {
      const note = input.value;
      serial(async () => { try { await request('cart/update.js', { note }); } catch (error) { cartFailures++; errors(cartScope(input), error.message); } });
    }
  });
  let quickViewController, quickViewVersion = 0;
  document.addEventListener('click', async event => {
    const button = event.target.closest('button, a'); if (!button) return;
    if (button.matches('[data-st-dialog-close]')) button.closest('dialog')?.close();
    if (button.matches('[data-st-popup-open]')) openDialog(document.getElementById(button.dataset.stPopupOpen));
    if (button.matches('[data-st-cart-open]')) { if (window.Studio?.config?.cartType !== 'page' && $('#studio-cart-dialog')) { event.preventDefault(); openDialog($('#studio-cart-dialog')); } }
    if (button.matches('[data-st-layout]')) button.closest('section').querySelector('.st-products-grid')?.classList.toggle('st-products-grid--list', button.dataset.stLayout === 'list');
    if (button.matches('[data-st-remove]')) {
      event.preventDefault();
      serial(async () => { try { await request('cart/change.js', { id: currentLineKey(button), quantity: 0 }); await refreshCart(); } catch (error) { cartFailures++; errors(cartScope(button), error.message); } });
    }
    if (button.matches('[data-st-discount-apply]')) {
      const scope = button.closest('[data-st-cart]'), code = $('[data-st-discount]', scope).value.trim();
      if (!code) return;
      button.disabled = true;
      serial(async () => {
        try {
          const cart = await request('cart/update.js', { discount: code });
          const applicable = cart.discount_codes?.some(item => item.code.toLowerCase() === code.toLowerCase() && item.applicable);
          await refreshCart();
          const currentScope = cartScope(scope);
          const field = currentScope && $('[data-st-discount]', currentScope); if (field) field.value = code;
          const result = currentScope && $('[data-st-discount-result]', currentScope); if (result) result.textContent = applicable ? '' : t('discount_not_applicable');
        } catch (error) { cartFailures++; errors(cartScope(scope), error.message); } finally { button.disabled = false; }
      });
    }
    if (button.matches('[data-st-share]') && button.dataset.stShare) {
      try { if (navigator.share) await navigator.share({ title: document.title, url: button.dataset.stShare }); else { await navigator.clipboard.writeText(button.dataset.stShare); button.textContent = t('link_copied'); } } catch {}
    }
    if (button.matches('[data-st-quick-view]')) {
      event.preventDefault();
      quickViewController?.abort(); quickViewController = new AbortController();
      const version = ++quickViewVersion;
      button.disabled = true;
      try {
        const endpoint = new URL(button.dataset.stQuickView, location.origin); endpoint.searchParams.set('section_id', 'quick-view');
        const response = await fetch(endpoint, { credentials: 'same-origin', signal: quickViewController.signal }); if (!response.ok) throw new Error();
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html'), content = parsed.querySelector('studio-product'); if (!content) throw new Error();
        if (version !== quickViewVersion || !button.isConnected) return;
        let dialog = $('#studio-quick-dialog'); if (!dialog) {
          dialog = document.createElement('dialog'); dialog.id = 'studio-quick-dialog'; document.body.append(dialog);
          dialog.addEventListener('close', () => { quickViewVersion++; quickViewController?.abort(); });
        }
        dialog.className = 'st-quick-dialog' + (window.Studio?.config?.quickView === 'drawer' ? ' st-quick-dialog--drawer' : '');
        dialog.replaceChildren(); const close = document.createElement('button'); close.type = 'button'; close.dataset.stDialogClose = ''; close.className = 'st-dialog-close'; close.textContent = '×'; close.setAttribute('aria-label', t('close')); dialog.append(close, content); openDialog(dialog);
      } catch (error) { if (error.name !== 'AbortError' && version === quickViewVersion && button.isConnected) location.assign(button.dataset.stQuickView); } finally { button.disabled = false; }
    }
    if (button.matches('[data-st-zoom]')) {
      event.preventDefault();
      let dialog = $('#studio-zoom-dialog'); if (!dialog) { dialog = document.createElement('dialog'); dialog.id = 'studio-zoom-dialog'; dialog.className = 'st-zoom-dialog'; document.body.append(dialog); }
      const image = document.createElement('img'); image.src = button.href; image.alt = $('img', button)?.alt || '';
      const close = document.createElement('button'); close.type = 'button'; close.dataset.stDialogClose = ''; close.textContent = '×'; close.setAttribute('aria-label', t('close')); dialog.replaceChildren(close, image); openDialog(dialog);
    }
    if (button.matches('[data-st-compare-add]') && !window.StudioCatalog) {
      const handle = button.dataset.stCompareAdd, items = readStore('studio.compare');
      writeStore('studio.compare', [...new Set([...items, handle])].slice(-4));
      if (!$('#studio-compare-dialog')) return;
      await $('studio-comparison').load(); openDialog($('#studio-compare-dialog'));
    }
    if (button.matches('[data-st-compare-remove]') && !window.StudioCatalog) {
      writeStore('studio.compare', readStore('studio.compare').filter(handle => handle !== button.dataset.stCompareRemove)); await button.closest('studio-comparison').load();
    }
  });
  document.addEventListener('click', event => { if (event.target.tagName === 'DIALOG') { const rect = event.target.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close(); } });
  window.StudioCommerce = { refreshCart, openCart: () => openDialog($('#studio-cart-dialog')), money };
})();
